import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAgentsApi } from '@/hooks/useAgentsApi';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Building2, Loader2, Mail, MapPin, Phone, Users } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

const statusColors: Record<string, string> = {
  active: 'bg-green-500/10 text-green-500 border-green-500/20',
  pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  past_due: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
};

export default function AgentAffiliateDetail() {
  const { affiliateId } = useParams<{ affiliateId: string }>();
  const navigate = useNavigate();
  const agentsApi = useAgentsApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ['agent-affiliate-detail', affiliateId],
    queryFn: async () => {
      if (!affiliateId) throw new Error('No affiliate ID');
      const response = await agentsApi.getAffiliateDetail(affiliateId);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    enabled: !!affiliateId,
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data?.affiliate) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
          <p className="text-destructive">
            {error instanceof Error ? error.message : 'Affiliate not found'}
          </p>
          <Button variant="outline" onClick={() => navigate('/agent/affiliates')}>
            Back to Affiliates
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const affiliate = data.affiliate;

  return (
    <DashboardLayout>
      {/* Back Button + Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => navigate('/agent/affiliates')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Affiliates
        </Button>
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-3">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gradient-gold">{affiliate.name}</h1>
            <p className="text-muted-foreground">Member since {formatDate(affiliate.created_at)}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Status & Subscription */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge
                variant="outline"
                className={statusColors[affiliate.subscription_status || ''] || ''}
              >
                {affiliate.subscription_status === 'active' && !affiliate.has_stripe_subscription
                  ? 'Approved (No Payment)'
                  : (affiliate.subscription_status || 'unknown').charAt(0).toUpperCase() +
                    (affiliate.subscription_status || 'unknown').slice(1).replace('_', ' ')}
              </Badge>
            </div>
            {affiliate.subscription_tier && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Tier</span>
                <span className="font-medium">{affiliate.subscription_tier}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Members</span>
              <span className="font-medium flex items-center gap-1">
                <Users className="h-4 w-4 text-muted-foreground" />
                {affiliate.member_count}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Owner Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-muted p-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium">{affiliate.owner.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-muted p-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <a
                  href={`mailto:${affiliate.owner.email}`}
                  className="font-medium text-primary hover:underline"
                >
                  {affiliate.owner.email}
                </a>
              </div>
            </div>
            {affiliate.owner.phone && (
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-muted p-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{affiliate.owner.phone}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Location */}
        {affiliate.address && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Location</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{affiliate.address}</p>
                  {affiliate.country_code && (
                    <p className="text-sm text-muted-foreground">{affiliate.country_code}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
